import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { CurationNiche, CurationRegion } from '@/lib/community-curation/types';

const NO_NICHE = 'none';

interface AddVideoDialogProps {
  open: boolean;
  niches: CurationNiche[];
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { url: string; nicheId: string | null; region: CurationRegion }) => void;
}

export function AddVideoDialog({
  open, niches, saving, onOpenChange, onSubmit,
}: AddVideoDialogProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [nicheId, setNicheId] = useState(NO_NICHE);
  const [region, setRegion] = useState<CurationRegion>('BR');

  useEffect(() => {
    if (open) {
      setUrl('');
      setNicheId(NO_NICHE);
      setRegion('BR');
    }
  }, [open]);

  // A niche decides the curation language; the region picker only applies
  // without one.
  const selectedNiche = niches.find((niche) => niche.id === nicheId) || null;
  const effectiveRegion = selectedNiche?.region || region;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.communityCuration.addVideoDialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.communityCuration.addVideoDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="curation-video-url">{t('admin.communityCuration.addVideoDialog.url')}</Label>
            <Input
              id="curation-video-url"
              value={url}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="curation-video-niche">{t('admin.communityCuration.addVideoDialog.niche')}</Label>
            <Select value={nicheId} onValueChange={setNicheId}>
              <SelectTrigger id="curation-video-niche">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_NICHE}>{t('admin.communityCuration.addVideoDialog.noNiche')}</SelectItem>
                {niches.map((niche) => (
                  <SelectItem key={niche.id} value={niche.id}>{niche.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="curation-video-region">{t('admin.communityCuration.addVideoDialog.region')}</Label>
            <Select
              value={effectiveRegion}
              onValueChange={(value) => setRegion(value as CurationRegion)}
              disabled={!!selectedNiche}
            >
              <SelectTrigger id="curation-video-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BR">{t('admin.communityCuration.regions.BR')}</SelectItem>
                <SelectItem value="US">{t('admin.communityCuration.regions.US')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('admin.communityCuration.form.cancel')}
          </Button>
          <Button
            disabled={saving || !url.trim()}
            onClick={() => onSubmit({
              url: url.trim(),
              nicheId: selectedNiche?.id || null,
              region: effectiveRegion,
            })}
          >
            {t('admin.communityCuration.addVideoDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
